
export enum ClassificationType {
  SDS = "SDS",
  AI_RESEARCH = "AI_RESEARCH",
}

/** A captured product image — stores the File for API backend call and a blob URL for display */
export interface CapturedImage {
  file: File;
  displayUrl: string;
}

export interface AsinImage {
  url: string;
}

export interface WasteProfile {
  id: string;
  confidence: number;
}

export interface SdsAttribute {
  name: string;
  value: string;
  reason: string;
}

export interface WasteProfileSource {
  name: string;
  sds_attributes?: SdsAttribute[];
}

export interface DonationCategory {
  id: string;
  name: string;
}

export interface SdsDocument {
  language?: string;
  url: string;
}

export interface Measurement {
  unit: string;
  value: number;
}

export interface Dimensions {
  height?: Measurement;
  length?: Measurement;
  width?: Measurement;
}

export interface HazmatStatement {
  value: string;
}

export interface ProductAttributes {
  weight?: Measurement;
  dimensions?: Dimensions;
  hazmat_state?: string;
  hazmat_statement?: HazmatStatement[];
  product_group?: string;
}

export interface ClassificationResponse {
  asin: string;
  name?: string;
  images?: AsinImage[];
  waste_profiles: WasteProfile[];
  waste_profile_source?: WasteProfileSource;
  donation_categories?: DonationCategory[];
  sds_list?: SdsDocument[];
  product_attributes?: ProductAttributes;
}

/**
 * Types for Container APIs
 * GET /classification/containers/site/{site-code}
 * POST /classification/add-to-container
 */

export interface MatchingContainer {
  id: string;
  name: string;
  waste_profile_id: string;
}

export interface AddToContainerRequest {
  asin: string;
  container_id: string;
}

export interface AddToContainerResponse {
  success?: boolean;
  message?: string;
}

// ─── AI Research (Product Classification) Types ───

/** Status of a waste classification session */
export enum WasteClassificationStatus {
  ANALYZING = "ANALYZING",
  COMPLETED = "COMPLETED",
  FAILED = "FAILED",
}

/** Image recognition output from the AI pipeline */
export interface ImageRecognitionResponse {
  product_name: string;
  matched_catalogue_id?: string;
  image_features: string[];
  inferred_ingredients: string[];
}

/** Waste profile returned by the AI classification */
export interface AIWasteProfile {
  profile_code: string;
}

/** AI-generated insight about the product */
export interface AIInsight {
  summary: string;
  visual_detection: string;
  ingredient_inference: string;
}

/** Waste classification output from the AI pipeline */
export interface WasteClassificationResponse {
  product_name: string;
  waste_profile: AIWasteProfile;
  ai_insight: AIInsight;
}

/** Combined science result from image recognition + waste classification */
export interface AIResearchResult {
  image_recognition: ImageRecognitionResponse;
  waste_classification: WasteClassificationResponse;
}

/** Response from POST /classification/ai-research */
export interface AIResearchResponse {
  classification_id: string;
  site_code: string;
  status: WasteClassificationStatus;
  captured_image_urls: string[];
  result: AIResearchResult;
}
